/* ------------------------------------------------------------------ *
 * features/dashboard/HeadDashboard.tsx
 *
 * The Head/Principal landing view, in the fees-section "glass" design
 * language: a gradient hero with glass status pills, a universal search,
 * four one-tap tiles with gradient badges, a row of pastel stat chips,
 * and soft insight cards (mark entry, top classes, open items, needs
 * attention, school roll) wired to live data via useHeadInsights. Every
 * chip/card links to a screen the Head can actually open.
 * ------------------------------------------------------------------ */
import {
  GraduationCap,
  ArrowRight,
  FileText,
  ClipboardCheck,
  BarChart3,
  Users,
  TrendingUp,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { GradientHero, GlassPill, StatChip } from '@/components/ui/glass';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { getPermissions } from '@/lib/permissions';
import { useGeneratedBatches } from '@/features/reports/useGeneratedBatches';
import { useHeadInsights } from './useHeadInsights';
import DashboardStudentSearch from './DashboardStudentSearch';
import type { TabId } from '@/components/Shell';

type Props = {
  onNavigate: (t: TabId, opts?: { studentId?: string }) => void;
  year: string;
  term: string;
};

/* ---------- small presentational helpers ---------- */
function Bar({ value, tone }: { value: number; tone: string }) {
  return (
    <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

function CardShell({
  title,
  dot,
  action,
  onAction,
  className = '',
  children,
}: {
  title: string;
  dot: string;
  action?: string;
  onAction?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      onClick={onAction}
      className={`rounded-[20px] border border-gray-100 dark:border-gray-700/70 bg-white dark:bg-gray-800/50 p-5 shadow-sm ${
        onAction ? 'cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5' : ''
      } ${className}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-white">
          <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
          {title}
        </div>
        {action && <span className="text-xs font-semibold text-[#2d5295] dark:text-blue-300">{action} →</span>}
      </div>
      {children}
    </div>
  );
}

const val = (v: number | null, loading: boolean, suffix = '') =>
  loading ? '—' : v == null ? '—' : `${v}${suffix}`;

export default function HeadDashboard({ onNavigate, year, term }: Props) {
  const { userData, userRoles } = useAuth();
  const { activeStudents, teachers, classes } = useData();
  const p = getPermissions(userRoles);
  const insights = useHeadInsights(year, term);
  const { batches } = useGeneratedBatches(year, term);

  const firstName = (userData?.displayName || '').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const activeClasses = classes.filter((c) => !c.archived);
  const generated = batches.length;
  const totalClasses = activeClasses.length;

  const primary = [
    { show: p.canViewAnyReport || p.canViewLiveAcademics, tab: 'viewReports' as TabId, icon: FileText, label: 'Reports', sub: 'View & read report cards', badge: 'from-[#1b3a6b] to-[#2d5295]' },
    { show: p.canViewProgress, tab: 'marksCompliance' as TabId, icon: ClipboardCheck, label: 'Marks Compliance', sub: 'Who has entered marks', badge: 'from-[#d4a92c] to-[#b8860b]' },
    { show: p.canViewProgress, tab: 'marksheet' as TabId, icon: BarChart3, label: 'Student Analysis', sub: 'Results, rankings & at-risk', badge: 'from-[#6d4bb0] to-[#8b5cf6]' },
    { show: p.canViewProgress, tab: 'teacherAnalysis' as TabId, icon: Users, label: 'Teacher Analysis', sub: 'Per-teacher & department', badge: 'from-[#0f766e] to-[#14b8a6]' },
  ].filter((a) => a.show);

  const { markEntry, atRisk, topClasses, needsAttention, openItems, attendanceToday, loading } = insights;
  const maxTop = topClasses[0]?.average || 100;

  return (
    <div className="space-y-5">
      <GradientHero
        icon={GraduationCap}
        title={firstName ? `${greeting}, ${firstName}` : greeting}
        subtitle="Search anyone, or tap through to reports, compliance and analysis"
        actions={
          <>
            {attendanceToday && <GlassPill tone="green">{attendanceToday.pct}% present today</GlassPill>}
            <GlassPill tone="gold">{generated} of {totalClasses} reports ready</GlassPill>
          </>
        }
      />

      {/* Universal search — students, teachers, classes */}
      <DashboardStudentSearch onNavigate={onNavigate} />

      {/* Primary tiles */}
      {primary.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {primary.map((a) => (
            <button
              key={a.tab}
              onClick={() => onNavigate(a.tab)}
              className="group text-left rounded-[20px] p-5 border border-gray-100 dark:border-gray-700/70 bg-white dark:bg-gray-800/50 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5"
            >
              <div className="flex items-center justify-between">
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white bg-gradient-to-br ${a.badge} shadow-md`}>
                  <a.icon size={24} />
                </span>
                <ArrowRight size={20} className="text-gray-300 dark:text-gray-600 group-hover:text-[#2d5295] group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="mt-4">
                <div className="text-lg font-bold text-gray-900 dark:text-white">{a.label}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{a.sub}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* At a glance — pastel stat chips */}
      <div className="flex items-center gap-3 pt-1">
        <h2 className="text-sm font-bold text-gray-800 dark:text-white">At a glance</h2>
        <span className="text-xs text-gray-400">this term · today</span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => onNavigate('marksCompliance')} className="text-left rounded-xl transition-transform hover:-translate-y-0.5">
          <StatChip tone="blue" value={val(markEntry.overall, loading, '%')} label="Mark entry complete" />
        </button>
        <button onClick={() => onNavigate('viewReports')} className="text-left rounded-xl transition-transform hover:-translate-y-0.5">
          <StatChip tone="emerald" value={<>{generated}<span className="text-base font-semibold text-gray-400"> / {totalClasses}</span></>} label="Reports generated" />
        </button>
        <button onClick={() => onNavigate('headInsight', { studentId: 'atRisk' })} className="text-left rounded-xl transition-transform hover:-translate-y-0.5">
          <StatChip tone="red" value={loading ? '—' : atRisk.total} label="Pupils at risk" />
        </button>
        {attendanceToday ? (
          <button onClick={() => onNavigate('headInsight', { studentId: 'absentToday' })} className="text-left rounded-xl transition-transform hover:-translate-y-0.5">
            <StatChip tone="gold" value={`${attendanceToday.pct}%`} label="Present today" />
          </button>
        ) : (
          <div className="rounded-xl"><StatChip tone="gold" value="—" label="No register today" /></div>
        )}
      </div>

      {/* Insight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Mark entry */}
        <CardShell title="Mark entry" dot="bg-amber-500" action="Compliance" onAction={() => onNavigate('marksCompliance')}>
          <div className="text-3xl font-extrabold text-gray-900 dark:text-white leading-none">
            {val(markEntry.overall, loading)}
            <span className="text-sm font-semibold text-gray-400 ml-1">overall</span>
          </div>
          <div className="mt-3 space-y-2">
            {([['CA', markEntry.ca], ['Exam', markEntry.exam], ['Comments', markEntry.comments]] as const).map(([lab, v]) => (
              <div key={lab} className="flex items-center gap-2.5">
                <span className="w-20 text-xs text-gray-500 dark:text-gray-400">{lab}</span>
                <div className="flex-1"><Bar value={v ?? 0} tone={(v ?? 0) >= 95 ? 'bg-gradient-to-r from-teal-500 to-emerald-600' : 'bg-gradient-to-r from-amber-400 to-[#c8991f]'} /></div>
                <span className="w-9 text-right text-xs font-semibold text-gray-700 dark:text-gray-200">{val(v, loading, '%')}</span>
              </div>
            ))}
          </div>
        </CardShell>

        {/* Top performing classes */}
        <CardShell title="Top performing classes" dot="bg-emerald-500" action="Full ranking" onAction={() => onNavigate('headInsight', { studentId: 'topClasses' })}>
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : topClasses.length === 0 ? (
            <p className="text-sm text-gray-400">No marks yet this term.</p>
          ) : (
            <div className="space-y-2.5">
              {topClasses.map((c) => (
                <div key={c.classId} className="flex items-center gap-2.5">
                  <span className="w-16 text-xs font-semibold text-gray-700 dark:text-gray-200 shrink-0">{c.name}</span>
                  <div className="flex-1"><Bar value={(c.average / (maxTop || 100)) * 100} tone={c.average >= c.passMark ? 'bg-gradient-to-r from-teal-500 to-emerald-600' : 'bg-gradient-to-r from-amber-400 to-[#c8991f]'} /></div>
                  <span className="w-9 text-right text-xs font-bold text-emerald-600 dark:text-emerald-300">{Math.round(c.average)}%</span>
                </div>
              ))}
            </div>
          )}
        </CardShell>

        {/* Open items — each row opens its own screen */}
        <CardShell title="Open items" dot="bg-amber-500">
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60 -my-1">
            {[
              { k: 'Comment flags to resolve', v: openItems.flags, tab: 'flags' as TabId, tone: openItems.flags > 0 ? 'text-amber-600' : 'text-emerald-600' },
              { k: 'Open discipline cases', v: openItems.discipline, tab: 'discipline' as TabId, tone: openItems.discipline > 0 ? 'text-amber-600' : 'text-emerald-600' },
              { k: 'Critical incidents this month', v: openItems.criticalThisMonth, tab: 'discipline' as TabId, tone: openItems.criticalThisMonth > 0 ? 'text-red-600' : 'text-gray-500' },
            ].map((r) => (
              <button
                key={r.k}
                onClick={() => onNavigate(r.tab)}
                className="w-full flex items-center justify-between py-2.5 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 px-1 -mx-1"
              >
                <span className="text-sm text-gray-600 dark:text-gray-300">{r.k}</span>
                <span className="flex items-center gap-1.5">
                  <span className={`font-bold text-sm ${r.tone}`}>{loading ? '—' : r.v}</span>
                  <ArrowRight size={13} className="text-gray-300 dark:text-gray-600" />
                </span>
              </button>
            ))}
          </div>
        </CardShell>

        {/* Needs attention — spans two columns */}
        <CardShell title="Needs attention" dot="bg-red-500" action="See all" onAction={() => onNavigate('headInsight', { studentId: 'needsAttention' })} className="sm:col-span-2">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : needsAttention.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-300"><TrendingUp size={16} /> Nothing below the pass mark.</div>
          ) : (
            <div className="space-y-2">
              {needsAttention.map((n) => (
                <div
                  key={n.key}
                  className={`flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl ${
                    n.severity === 'crit'
                      ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/25 dark:to-red-900/10'
                      : 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/25 dark:to-amber-900/10'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800 dark:text-white truncate">{n.label}</p>
                    <p className={`text-xs mt-0.5 ${n.severity === 'crit' ? 'text-red-600 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>{n.sub}</p>
                  </div>
                  <span className={`shrink-0 rounded-lg font-extrabold px-2.5 py-1 ${n.severity === 'crit' ? 'bg-[#e53935] text-white text-sm' : 'bg-[#dd9a2a] text-[#3a2c08] text-xs'}`}>{n.value}</span>
                </div>
              ))}
            </div>
          )}
        </CardShell>

        {/* School roll */}
        <CardShell title="School roll" dot="bg-[#2d5295]">
          <div className="grid grid-cols-3 gap-2 text-center pt-1">
            {[
              { n: activeStudents.length, l: 'Pupils' },
              { n: teachers.length, l: 'Staff' },
              { n: totalClasses, l: 'Classes' },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-2xl font-extrabold text-gray-900 dark:text-white leading-none">{s.n}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">{s.l}</div>
              </div>
            ))}
          </div>
        </CardShell>
      </div>
    </div>
  );
}
